import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type {
  PermessiAdminRuoloRead,
  PermessiAdminUtenteRead,
  PermessiPut,
  Ruolo,
} from "@/types/api";

// ── Preset per ruolo ────────────────────────────────────────────────

export function useAdminRuoloPermessi(ruolo: Ruolo) {
  return useQuery({
    queryKey: ["admin", "ruolo-permessi", ruolo],
    queryFn: () =>
      apiFetch<PermessiAdminRuoloRead>(
        `/api/v1/admin/ruoli/${encodeURIComponent(ruolo)}/permessi`
      ),
    staleTime: 30_000,
  });
}

export function usePutAdminRuoloPermessi(ruolo: Ruolo) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PermessiPut) =>
      apiFetch<PermessiAdminRuoloRead>(
        `/api/v1/admin/ruoli/${encodeURIComponent(ruolo)}/permessi`,
        { method: "PUT", body: JSON.stringify(body) }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "ruolo-permessi", ruolo] });
      // Invalidare anche eventuali utenti che hanno questo ruolo
      qc.invalidateQueries({ queryKey: ["admin", "utente-permessi"] });
      qc.invalidateQueries({ queryKey: ["me", "permessi"] });
    },
  });
}

// ── Override per utente ─────────────────────────────────────────────

export function useAdminUtentePermessi(email: string) {
  return useQuery({
    queryKey: ["admin", "utente-permessi", email],
    queryFn: () =>
      apiFetch<PermessiAdminUtenteRead>(
        `/api/v1/admin/utenti/${encodeURIComponent(email)}/permessi`
      ),
    enabled: !!email,
    staleTime: 30_000,
  });
}

export function usePutAdminUtentePermessi(email: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PermessiPut) =>
      apiFetch<PermessiAdminUtenteRead>(
        `/api/v1/admin/utenti/${encodeURIComponent(email)}/permessi`,
        { method: "PUT", body: JSON.stringify(body) }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "utente-permessi", email] });
      qc.invalidateQueries({ queryKey: ["me", "permessi"] });
    },
  });
}
